'use client';

import { useId } from 'react';

import { cn, tv } from '@/libs';

type RadioButtonProps = {
  label?: string;
  size?: 'small' | 'regular' | 'large';
  disabled?: boolean;
  className?: string;
  labelClassName?: string;
  value: string | number;
  name: string;
  checked: boolean;
  onChangeAction: (value: string | number) => void;
};

const wrapper = tv({
  base: 'inline-flex items-center gap-2 select-none',
  variants: {
    disabled: {
      true: 'opacity-50 cursor-not-allowed',
    },
  },
});

const outerCircle = tv({
  base: 'rounded-full border border-primary transition-colors flex items-center justify-center',
  variants: {
    size: {
      small: 'w-4 h-4',
      regular: 'w-5 h-5',
      large: 'w-6 h-6',
    },
  },
  defaultVariants: {
    size: 'regular',
  },
});

export const RadioButton = ({
  label,
  size = 'regular',
  disabled = false,
  className,
  labelClassName,
  value,
  name,
  checked,
  onChangeAction,
}: RadioButtonProps) => {
  const id = useId();

  return (
    <label className={cn(wrapper({ disabled }), 'cursor-pointer', className)}>
      <input
        id={id}
        name={name}
        type="radio"
        value={String(value)}
        checked={checked}
        onChange={() => onChangeAction(value)}
        className="sr-only peer"
        disabled={disabled}
      />

      <span
        className={cn(
          outerCircle({ size }),
          'relative bg-foreground border-transparent peer-checked:bg-primary before:content-[""] before:block before:transition-transform before:duration-200 before:w-1/2 before:h-1/2 before:absolute before:scale-0 before:bg-current before:rounded-full peer-checked:before:scale-100 ',
        )}
      />

      {label && <p className={cn('text-foreground text-sm', labelClassName)}>{label}</p>}
    </label>
  );
};
